import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, Lock, Loader2, CheckCircle, AlertCircle, Eye, EyeOff, UserPlus } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const SetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const { theme, setTheme } = useTheme();
  const previousThemeRef = useRef(theme);
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [tokenInfo, setTokenInfo] = useState(null);
  
  const [form, setForm] = useState({
    password: '',
    confirmPassword: ''
  });

  // Force dark mode on this page
  useEffect(() => {
    previousThemeRef.current = theme;
    if (theme !== 'dark') {
      setTheme('dark');
    }
    return () => {
      if (previousThemeRef.current !== 'dark') {
        setTheme(previousThemeRef.current);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Validate invitation token on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setTokenError('No invitation token provided');
        setLoading(false);
        return;
      }
      
      try {
        const response = await axios.get(`${API_URL}/auth/validate-invitation-token/${token}`);
        setTokenValid(true);
        setTokenInfo(response.data);
      } catch (error) {
        setTokenError(error.response?.data?.detail || 'Invalid or expired invitation link');
      } finally {
        setLoading(false);
      }
    };
    
    validateToken();
  }, [token]);

  const validatePassword = () => {
    if (form.password.length < 8) {
      return 'Password must be at least 8 characters';
    }
    if (!/\d/.test(form.password)) {
      return 'Password must contain at least one number';
    }
    if (form.password !== form.confirmPassword) {
      return 'Passwords do not match';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationError = validatePassword();
    if (validationError) {
      toast.error(validationError);
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/auth/set-password`, {
        token,
        password: form.password
      });
      
      setSuccess(true);
      toast.success('Password set successfully! You can now log in.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set password');
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#ffd108]"></div>
      </div>
    );
  }

  // Invalid token state
  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-gray-900/50 border-gray-800">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <div className="p-3 bg-red-500/10 rounded-full inline-block">
                <AlertCircle className="h-8 w-8 text-red-500" />
              </div>
              <h2 className="text-xl font-semibold text-white">Invalid Invitation</h2>
              <p className="text-gray-400">{tokenError}</p>
              <p className="text-sm text-gray-500">
                This invitation link may have expired or already been used. 
                Please contact your administrator for a new invitation.
              </p>
              <Link to="/login">
                <Button variant="outline" className="mt-4">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Login
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-gray-900/50 border-gray-800">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <div className="p-3 bg-green-500/10 rounded-full inline-block">
                <CheckCircle className="h-8 w-8 text-green-500" />
              </div>
              <h2 className="text-xl font-semibold text-white">You're All Set!</h2>
              <p className="text-gray-400">
                Your password has been set successfully. You can now log in to your account.
              </p>
              {tokenInfo && (
                <div className="bg-gray-800/50 rounded-lg p-4 text-left mt-4">
                  <p className="text-sm text-gray-400 mb-2">Your login details:</p>
                  <p className="text-sm text-white">
                    <span className="text-gray-500">Client ID:</span> <span className="font-mono">{tokenInfo.client_id}</span>
                  </p>
                  <p className="text-sm text-white">
                    <span className="text-gray-500">Username:</span> <span className="font-mono">{tokenInfo.username}</span>
                  </p>
                </div>
              )}
              <Link to="/login">
                <Button className="btn-volt mt-4 w-full">
                  Continue to Login
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Set password form
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-gray-900/50 border-gray-800">
        <CardHeader className="text-center">
          <div className="mx-auto p-3 bg-[#ffd108]/10 rounded-full inline-block mb-2">
            <UserPlus className="h-8 w-8 text-[#ffd108]" />
          </div>
          <CardTitle className="text-2xl text-white">Welcome to EQUIPR CORE</CardTitle>
          <CardDescription className="text-gray-400">
            Set your password to complete your account setup
          </CardDescription>
        </CardHeader>
        <CardContent>
          {tokenInfo && (
            <div className="bg-gray-800/50 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-400 mb-2">Setting password for:</p>
              <p className="text-white font-medium">{tokenInfo.full_name || tokenInfo.email}</p>
              <p className="text-sm text-gray-500">{tokenInfo.company_name}</p>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-300">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={(e) => setForm(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="Enter your password"
                  className="bg-gray-800 border-gray-700 text-white pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-gray-300">Confirm Password</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={form.confirmPassword}
                  onChange={(e) => setForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  placeholder="Confirm your password"
                  className="bg-gray-800 border-gray-700 text-white pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            
            <div className="text-xs text-gray-500 space-y-1">
              <p>Password requirements:</p>
              <ul className="list-disc list-inside">
                <li>At least 8 characters</li>
                <li>At least one number</li>
              </ul>
            </div>
            
            <Button 
              type="submit" 
              className="btn-volt w-full" 
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Setting Password...
                </>
              ) : (
                <>
                  <Lock className="h-4 w-4 mr-2" />
                  Set Password
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default SetPassword;
